import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { adminFetch } from '@/lib/apiClient';
import { idempotencyKeyFor } from '@/lib/http-core';
import {
  DEFAULT_CART_POLICY,
  type CartPolicyPayload,
  type CartPolicySettings,
} from '../types/shoppingPolicy';

/**
 * 장바구니 정책 GET/PUT — order-service admin(고도몰 ID51_3 "장바구니 설정").
 * 게이트웨이 `/admin/v1/orders/**` → BE `/api/admin/v1/orders/**` RewritePath 재사용.
 * v2 envelope `{ success, data, error, meta }`. PUT 은 Idempotency-Key 수용(재시도 안전).
 *
 * 경로가 generated OpenAPI 스펙에 미반영이라 `adminFetch`(401 자동복구 fetch — single-flight
 * refresh + 1회 retry)로 호출한다. adminHeaders + credentials 는 adminFetch 내부에서 부착.
 */
const PATH = '/admin/v1/orders/cart-policy-settings';

interface CartEnvelope {
  success: boolean;
  data?: CartPolicySettings | null;
}

/**
 * 응답을 정규화한다 — BE 가 일부 필드를 누락/널로 줄 수 있어 기본값으로 채워 폼이 항상
 * controlled 상태를 유지하도록 한다(undefined → uncontrolled 경고 방지).
 */
const normalize = (s: CartPolicySettings | null | undefined): CartPolicySettings => ({
  cartAddRedirect: s?.cartAddRedirect ?? DEFAULT_CART_POLICY.cartAddRedirect,
  guestCartEnabled: s?.guestCartEnabled ?? DEFAULT_CART_POLICY.guestCartEnabled,
  cartRetentionDays: s?.cartRetentionDays ?? DEFAULT_CART_POLICY.cartRetentionDays,
  cartDupPolicy: s?.cartDupPolicy ?? DEFAULT_CART_POLICY.cartDupPolicy,
  cartSoldoutPolicy: s?.cartSoldoutPolicy ?? DEFAULT_CART_POLICY.cartSoldoutPolicy,
  cartMaxQtyPerItem: s?.cartMaxQtyPerItem ?? DEFAULT_CART_POLICY.cartMaxQtyPerItem,
  // 0 = 무제한이라 ?? 만 사용(0 은 유효값, ?? 는 null/undefined 만 폴백).
  cartMaxItems: s?.cartMaxItems ?? DEFAULT_CART_POLICY.cartMaxItems,
  updatedBy: s?.updatedBy ?? null,
  updatedAt: s?.updatedAt ?? null,
});

export const cartPolicyQueryKey = () => ['admin', 'cart-policy-settings'] as const;

const fetchSettings = async (): Promise<CartPolicySettings> => {
  const res = await adminFetch(PATH);
  if (!res.ok) throw new Error(`cart policy settings fetch failed: ${res.status}`);
  const env = (await res.json()) as CartEnvelope;
  return normalize(env.data);
};

export const useCartPolicyQuery = () =>
  useQuery<CartPolicySettings>({
    queryKey: cartPolicyQueryKey(),
    queryFn: fetchSettings,
  });

export const useCartPolicyMutation = () => {
  const qc = useQueryClient();
  return useMutation<CartPolicySettings, Error, CartPolicyPayload>({
    mutationFn: async (payload) => {
      const res = await adminFetch(PATH, {
        method: 'PUT',
        headers: { 'Idempotency-Key': idempotencyKeyFor('PUT', PATH, payload) },
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error(`cart policy settings update failed: ${res.status}`);
      const env = (await res.json()) as CartEnvelope;
      return normalize(env.data);
    },
    onSuccess: (data) => {
      qc.setQueryData(cartPolicyQueryKey(), data);
      void qc.invalidateQueries({ queryKey: cartPolicyQueryKey() });
    },
  });
};
